import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { RootState } from '../../lib/store';
import { logout as serviceLogout } from '../../lib/store/common-actions';
import { FeatureFlags } from '../../lib/util/flags/types';

import ArrowBack from '../../assets/img/blue_icon-arrow-left.svg';
import SearchIconMobile from '../../assets/img/icon-search_24px.svg';
import { useFlags } from '../../hooks';
import MobileSubPageTitleHeader from '../../shared_components/Header/components/MobileSubPageTitleHeader/MobileSubPageTitleHeader';
import SharedHeader from '../../shared_components/Header/Header';
import { getSubPageName, getTabName } from '../../util/helpers';
import { UI_ROUTES } from '../../util/routes';
import { ClickableImage } from '../index';

import HeaderDropdown from './HeaderDropdown/HeaderDropdown';
import MobileSymbolDetailsHeader from './MobileSymbolDetailsHeader/MobileSymbolDetailsHeader';
import { LogoutActionSelector } from './HeaderConstants';

import styles from '../../shared_components/Header/Header.module.scss';

type OwnProps = {
  isSearchActive: (val: boolean) => void;
};
type Props = OwnProps & ReduxProps;

const Header = (props: Props) => {
  const { logout, isSearchActive } = props;
  const flags = useFlags([FeatureFlags.referrals]);
  const isReferralsEnabled = flags[FeatureFlags.referrals].enabled;
  const location = useLocation();
  const navigate = useNavigate();

  const isShowSearchIcon = [
    UI_ROUTES.earningsCalendar,
    UI_ROUTES.overview,
    UI_ROUTES.discover,
    UI_ROUTES.dashboard,
    UI_ROUTES.referrals,
    UI_ROUTES.payouts,
    UI_ROUTES.myProfile,
    UI_ROUTES.tradeHistory,
  ].includes(location.pathname as UI_ROUTES);

  const handleGoBack = () => {
    navigate(-1);
  };

  // Only dispatch logout - the BFF logout redirect brings the user back to the app,
  // where the normal unauthenticated flow takes over. Dispatching login() here would
  // start a competing navigation and cancel the logout redirect.
  const handleLogout = () => {
    logout();
  };

  const returnBtn = () => (
    <button type="button" className={styles.returnBtn} onClick={handleGoBack}>
      <img src={ArrowBack} alt="Back" />
    </button>
  );

  const onSearchClickWeb = (): void => {
    isSearchActive(true);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'k') {
        e.preventDefault();
        onSearchClickWeb();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const onSearchClickMob = () => {
    navigate(`${UI_ROUTES.searchSymbolsMob}`);
  };

  const isSymbolDetailScreen = location.pathname.startsWith(`${UI_ROUTES.symbol}/`);
  const symbol = isSymbolDetailScreen ? location.pathname.split('/')[2] : '';
  const subPageName = getSubPageName(location);
  const tabName = getTabName(location, isReferralsEnabled);

  const renderMobileActions = () => (!isShowSearchIcon ? null : (
    <>
      <div className={styles.searchBtnMobile}>
        <ClickableImage
          src={SearchIconMobile}
          alt="search"
          className={styles.searchIcon}
          onClick={onSearchClickMob}
        />
      </div>
      <div className={styles.notificationsMobile}>
        <HeaderDropdown isUserProfile={false} />
      </div>
    </>
  ));

  const renderDesktopActions = () => (
    <>
      <div className={styles.settings}>
        <HeaderDropdown isUserProfile={false} />
      </div>
      <div className={styles.userData}>
        <HeaderDropdown isUserProfile actions={{ [LogoutActionSelector]: handleLogout }} />
      </div>
    </>
  );

  return (
    <SharedHeader
      primaryControl={subPageName || tabName === 0 ? returnBtn() : tabName}
      centerContent={(
        <>
          {isSymbolDetailScreen && symbol && <MobileSymbolDetailsHeader symbol={symbol} />}
          {!isSymbolDetailScreen && subPageName && <MobileSubPageTitleHeader title={subPageName} />}
        </>
      )}
      mobileActions={renderMobileActions()}
      desktopActions={renderDesktopActions()}
      isSearchVisible={isShowSearchIcon}
      onSearchClick={onSearchClickWeb}
      isSymbolDetailScreen={isSymbolDetailScreen}
    />
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
  logout: () => dispatch(serviceLogout()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Header);
